/**
 * LeadForge France - SEO Landing Page
 * Dynamic page for /leads-{category}-{location}
 */
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { seoAPI, pricingAPI } from '../lib/api';
import { useConfig } from '../lib/config';
import { events } from '../lib/analytics';
import { Button } from '../components/ui/button';
import {
  Database,
  MapPin,
  ArrowRight,
  CheckCircle,
  Mail,
  Phone,
  Globe,
  Star,
  Building2,
  Loader2,
} from 'lucide-react';

const SEOLandingPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { config } = useConfig();
  
  const [pageData, setPageData] = useState(null);
  const [priceTiers, setPriceTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Parse slug: leads-{category}-{location}
  const parseSlug = (fullSlug) => {
    if (!fullSlug || !fullSlug.startsWith('leads-')) return null;
    
    const parts = fullSlug.replace('leads-', '').split('-');
    if (parts.length < 2) return null;
    
    // Last part is always location
    const location = parts[parts.length - 1];
    // Everything else is category
    const category = parts.slice(0, -1).join('-');
    
    return { category, location };
  };

  useEffect(() => {
    const loadPageData = async () => {
      const parsed = parseSlug(slug);
      if (!parsed) {
        setError('Page non trouvée');
        setLoading(false);
        return;
      }

      try {
        const [pageRes, priceRes] = await Promise.all([
          seoAPI.getPageData(parsed.category, parsed.location),
          pricingAPI.getTiers(),
        ]);
        setPageData(pageRes.data);
        setPriceTiers(priceRes.data);
        
        // Update document title
        if (pageRes.data?.meta?.title) {
          document.title = pageRes.data.meta.title;
        }
        
        events.CATEGORY_VIEW(parsed.category);
      } catch (err) {
        console.error('Error loading SEO page:', err);
        setError('Page non trouvée');
      } finally {
        setLoading(false);
      }
    };

    loadPageData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#2563EB]" />
      </div>
    );
  }

  if (error || !pageData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-[#0F172A] mb-4">404</h1>
          <p className="text-[#64748B] mb-8">{error || 'Page non trouvée'}</p>
          <Link to="/">
            <Button className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white">Retour à l'accueil</Button>
          </Link>
        </div>
      </div>
    );
  }

  const { category, location, lead_count, meta } = pageData;

  return (
    <>
    <Helmet>
      <title>{pageData ? `Leads ${pageData.category.name} ${pageData.location.name} | LeadForge France` : 'Leads B2B France | LeadForge'}</title>
      <meta name="description" content={pageData ? `Achetez des leads ${pageData.category.name} a ${pageData.location.name}. ${pageData.lead_count} entreprises disponibles avec email, telephone et site web. Donnees INSEE SIRENE.` : 'Leads B2B qualifies en France'} />
      <link rel="canonical" href={`https://leadforgefrance.com/secteur/${pageData?.category?.slug || ''}`} />
    </Helmet>
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-b from-[#F8FAFC] to-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            {/* Breadcrumb */}
            <nav className="flex items-center justify-center gap-2 text-sm text-[#64748B] mb-8">
              <Link to="/" className="hover:text-[#2563EB]">Accueil</Link>
              <span>/</span>
              <Link to="/categories" className="hover:text-[#2563EB]">Catégories</Link>
              <span>/</span>
              <span className="text-[#0F172A] font-medium">{category.name} {location.name}</span>
            </nav>

            <div className="inline-flex items-center gap-2 bg-[#2563EB]/10 text-[#2563EB] rounded-full px-4 py-2 mb-6 text-sm font-medium">
              <Database className="w-4 h-4" />
              <span>Données INSEE SIRENE</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#0F172A] mb-6">
              Leads {category.name}
              <span className="text-[#2563EB] block">{location.name}</span>
            </h1>

            <p className="text-lg lg:text-xl text-[#64748B] mb-8 max-w-2xl mx-auto">
              Accédez à {lead_count.toLocaleString('fr-FR')} {category.name.toLowerCase()} 
              {' '}à {location.name}. Emails, téléphones et coordonnées vérifiées 
              issues du registre officiel INSEE SIRENE.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
              <Button 
                onClick={() => navigate(`/purchase?category=${category.category_id}`)}
                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-lg h-14 px-8 font-bold"
                data-testid="seo-cta-btn"
              >
                Commencer maintenant
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate('/categories')}
                className="text-lg h-14 px-8 border-2 border-[#0F172A] font-semibold"
              >
                Explorer les catégories
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 max-w-xl mx-auto">
              <div className="text-center">
                <p className="text-3xl font-bold text-[#0F172A] mono">
                  {lead_count.toLocaleString('fr-FR')}
                </p>
                <p className="text-sm text-[#64748B]">Leads disponibles</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-[#0F172A]">100%</p>
                <p className="text-sm text-[#64748B]">Vérifiés</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-[#0F172A]">24h</p>
                <p className="text-sm text-[#64748B]">Support</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-16 bg-[#0F172A]">
        <div className="container-custom">
          <h2 className="text-2xl font-bold text-white text-center mb-10">
            Chaque lead comprend
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {[
              { icon: Building2, text: 'Nom de l\'entreprise' },
              { icon: MapPin, text: 'Adresse complète' },
              { icon: Phone, text: 'Téléphone' },
              { icon: Mail, text: 'Email vérifié' },
              { icon: Star, text: 'Score qualité' },
            ].map((item, index) => (
              <div key={index} className="flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-[#2563EB] rounded-xl flex items-center justify-center mb-3">
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <p className="text-white text-sm">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Example Lead */}
      <section className="py-20">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[#0F172A] mb-4">
                Exemple de lead {category.name.toLowerCase()} à {location.name}
              </h2>
              <p className="text-lg text-[#64748B] mb-6">
                Voici un exemple représentatif des données que vous obtiendrez. 
                Chaque lead est enrichi et vérifié pour garantir la qualité de votre prospection.
              </p>
              <ul className="space-y-3">
                {[
                  'Données issues du registre officiel INSEE SIRENE',
                  'Email professionnel vérifié',
                  'Numéro de téléphone direct',
                  'Score de qualité pour prioriser vos actions',
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-[#64748B]">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-2xl shadow-xl border border-[#E2E8F0] p-8">
              <div className="flex items-center gap-4 mb-6 pb-6 border-b border-[#E2E8F0]">
                <div className="w-14 h-14 bg-[#2563EB]/10 rounded-xl flex items-center justify-center">
                  <Building2 className="w-7 h-7 text-[#2563EB]" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#0F172A]">
                    {category.name} Exemple
                  </h3>
                  <p className="text-[#64748B]">{category.name}</p>
                </div>
                <div className="ml-auto">
                  <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
                    <Star className="w-4 h-4" />
                    89%
                  </span>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <MapPin className="w-5 h-5 text-[#64748B]" />
                  <span className="text-[#0F172A]">{location.name}</span>
                </div>
                <div className="flex items-center gap-4">
                  <Phone className="w-5 h-5 text-[#64748B]" />
                  <span className="text-[#0F172A] mono">XX XX XX XX XX</span>
                  <CheckCircle className="w-4 h-4 text-green-500 ml-auto" />
                </div>
                <div className="flex items-center gap-4">
                  <Mail className="w-5 h-5 text-[#64748B]" />
                  <span className="text-[#0F172A]">contact@exemple.fr</span>
                  <CheckCircle className="w-4 h-4 text-green-500 ml-auto" />
                </div>
                <div className="flex items-center gap-4">
                  <Globe className="w-5 h-5 text-[#64748B]" />
                  <span className="text-[#2563EB]">www.exemple.fr</span>
                  <CheckCircle className="w-4 h-4 text-green-500 ml-auto" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#0F172A] mb-4">
              Tarifs pour les leads {category.name.toLowerCase()} à {location.name}
            </h2>
            <p className="text-lg text-[#64748B]">
              Choisissez le volume adapté à vos besoins
            </p>
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
            {priceTiers.map((tier, index) => (
              <div
                key={tier.volume}
                className={`bg-white rounded-2xl border-2 p-6 text-center ${
                  tier.is_popular ? 'border-[#2563EB] shadow-xl scale-105 relative' : 'border-[#E2E8F0]'
                }`}
              >
                {tier.is_popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#2563EB] text-white text-xs font-bold px-3 py-1 rounded-full">
                    Populaire
                  </div>
                )}
                <p className="text-3xl font-bold text-[#0F172A] mono mb-1">
                  {tier.volume.toLocaleString('fr-FR')}
                </p>
                <p className="text-[#64748B] text-sm mb-4">leads</p>
                <p className="text-2xl font-bold text-[#0F172A] mb-1">
                  {tier.total_price.toFixed(0)}€
                </p>
                <p className="text-xs text-[#64748B] mono mb-4">
                  {tier.price_per_lead.toFixed(2)}€/lead
                </p>
                <Button
                  onClick={() => navigate(`/purchase?category=${category.category_id}&volume=${tier.volume}`)}
                  className={`w-full ${tier.is_popular ? 'bg-[#2563EB] hover:bg-[#1D4ED8] text-white' : 'bg-[#0F172A] text-white'}`}
                  size="sm"
                >
                  Sélectionner
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0F172A]">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Prêt à prospecter les {category.name.toLowerCase()} à {location.name} ?
          </h2>
          <p className="text-lg text-[#94A3B8] mb-8 max-w-2xl mx-auto">
            Accédez à {lead_count.toLocaleString('fr-FR')} leads qualifiés dès maintenant.
          </p>
          <Button 
            onClick={() => navigate(`/purchase?category=${category.category_id}`)}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-lg h-14 px-8 font-bold"
          >
            Commencer maintenant
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>
    </div>
    </>
  );
};

export default SEOLandingPage;